import { useQuery } from '@tanstack/react-query'
import { Fragment, useEffect, useRef, useState } from 'react'
import { ticketConnectionQualityQuery } from '../api/connectionQuality'
import { ticketDevicesQuery, ticketSummaryQuery, type TicketLicense, type TicketStagesBand, type TicketSummaryPage } from '../api/easeedesk'
import { Spinner } from '../ui/Spinner'
import { TicketConnectionQuality } from './TicketConnectionQuality'
import { TicketDevicePanel } from './TicketDevicePanel'
import { TicketDevices } from './TicketDevices'
import { TicketStages } from './TicketStages'
import { Attachments, CustomFields, Feedback, Images, LastUpdates, Operations, Section, Subscribers, Times } from './TicketSummaryBlocks'

/**
 * Fila "Summary" - `Easeedesk::ticketSummary()` cu
 * `tickets/details/summary.php`.
 *
 * Doua coloane, ca in macheta veche: in stanga cine a cerut si ce a cerut, in
 * dreapta masinile. Pana la livrarea asta, fila deschidea DOAR coloana din
 * dreapta - panoul de echipamente - fiindca numai el fusese mutat; blocurile
 * "User Details" si "Current Request", adica primul lucru pe care il citeste
 * cineva care desface un tichet, lipseau cu totul.
 *
 * Blocurile se strang si se desfac de pe sageata din capul lor, nu de pe titlu:
 * `.toggle-details` sta pe `<i>`, si numai el are `cursor: pointer`. Sageata in
 * sus inseamna desfacut.
 *
 * Toate cele noua blocuri ale coloanei din stanga sunt aici, in ordinea
 * machetei: cele doua de mai jos, apoi cele sapte din `TicketSummaryBlocks.tsx`
 * (Last Updates, Images, Attachments, Subscribers, Operations Summary, Times of
 * Operations, Custom fields, Feedback). Ce era gol in macheta nu se deseneaza.
 */
export function TicketSummary({ id }: { id: number }) {
  const { data, error } = useQuery(ticketSummaryQuery(id))
  /* Aceleasi cereri ca blocurile din dreapta (aceeasi cheie, deci o singura
     cerere): de aici se afla daca dreapta are ceva de aratat. */
  const devices = useQuery(ticketDevicesQuery(id))
  const cq = useQuery(ticketConnectionQualityQuery(id))

  if (error) {
    return (
      <div className="note note-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> The summary could not be loaded.
      </div>
    )
  }
  if (!data) return <Spinner />

  const right = rightColumn(devices, cq, data.stages)

  /*
   * Blocurile de sub descriere. Cand dreapta n-are NIMIC de aratat, se muta in
   * dreapta, ca sa nu ramana jumatate de ecran goala; cand are macar un bloc,
   * raman in stanga, ca in macheta veche.
   *
   * Hotararea se ia numai dupa ce s-a stabilit ce are dreapta (masinile si
   * Connection Quality vin din cereri separate). Pana atunci blocurile nu se
   * deseneaza deloc - dreapta arata rotita masinilor - ca sa nu apara intr-o
   * coloana si sa sara apoi in cealalta.
   *
   * Imaginile si atasamentele NU se muta: stau mereu in stanga, imediat sub
   * descriere, fiindca tin de ce a scris omul (decizia din 13 oct 2026).
   */
  const rest = (
    <>
      <LastUpdates comments={data.comments} />
      <Subscribers names={data.subscribers} />
      <Operations operations={data.operations} />
      <Times times={data.times} />
      <CustomFields fields={data.customFields} />
      <Feedback feedback={data.feedback} />
    </>
  )

  return (
    <div className="row">
      <div className="col-md-6">
        <UserDetails page={data} />
        <CurrentRequest id={id} subject={data.subject} description={data.description} />
        <Images ticket={id} images={data.images} />
        <Attachments files={data.files} />
        {right === 'full' && rest}
      </div>

      {/* Coloana din dreapta a rezumatului: masinile, cerute separat, apoi
          drumul cererii - in ordinea machetei vechi: echipamentele, drumul,
          graficele. Sus in coloana, nu la coada ei: dupa "cine si pe ce"
          urmeaza "unde a ajuns". */}
      <div className="col-md-6">
        <TicketDevices id={id} />
        {/* Connection Quality: legatura aparatului in cele 30 de minute dinaintea
            tichetului. Nu apare cand tichetul n-are aparat cu masuratori CQ. */}
        <TicketConnectionQuality id={id} />
        <TicketStages band={data.stages} />
        {/* Masina pe scurt (sanatate, configuratie, captura la cerere,
            alarme, schimbari) - `summary/device_widgets.php` din portal. Numai
            pe tichetele cu aparat; fiecare bloc isi cere singur datele. */}
        <TicketDevicePanel ticket={id} />
        {right === 'empty' && rest}
      </div>
    </div>
  )
}

/**
 * Ce are coloana din dreapta: `pending` cat timp o cerere a ei inca n-a
 * raspuns, `empty` cand niciun bloc al ei nu se deseneaza, `full` altfel.
 * Regulile sunt ale blocurilor insesi: `TicketDevices` tace numai pe
 * `empty: true` (o eroare o arata), `TicketConnectionQuality` tace pe eroare
 * si pe `available: false`, `TicketStages` pe un drum fara stadii.
 *
 * Blocurile masinii (`TicketDevicePanel`) se deseneaza cand tichetul are macar
 * un aparat - si atunci dreapta e plina, chiar daca "User Devices" ar tacea.
 * Nu se asteapta raspunsul blocurilor ei: masina tichetului e destul ca sa se
 * stie ca dreapta are ce arata, iar blocurile vin dupa, fara sa mute nimic.
 */
export function rightColumn(
  devices: { data?: { empty: boolean; affected?: { devices: unknown[] } }; isError: boolean },
  cq: { data?: { available: boolean }; isError: boolean },
  stages: TicketStagesBand | null,
): 'pending' | 'empty' | 'full' {
  if (devices.isError) return 'full'
  if (!devices.data) return 'pending'
  if ((devices.data.affected?.devices.length ?? 0) > 0) return 'full'
  if (!devices.data.empty) return 'full'
  if (stages !== null && stages.stages.length > 0) return 'full'
  if (cq.isError) return 'empty'
  if (!cq.data) return 'pending'
  return cq.data.available ? 'full' : 'empty'
}

function UserDetails({ page }: { page: TicketSummaryPage }) {
  /*
   * "Customer Name" (in portal "WO Customer Name") se ascunde cand exista SI manager IT, SI cel putin un
   * tehnician - randul ar spune atunci a treia oara acelasi lucru. Hotararea e
   * a machetei vechi, si ramane in macheta.
   */
  const hideCreator = page.manager !== null && page.technicians.length > 0

  return (
    <Section title="User Details">
      <table width="100%">
        <tbody>
          <tr>
            <td>
              <table className="details-table" width="100%">
                <tbody>
                  <Registered page={page} />

                  <tr className={hideCreator ? 'hidden' : undefined}>
                    <td className="title">Customer Name</td>
                    <td>{page.creator}</td>
                  </tr>

                  {page.manager && (
                    <tr>
                      <td className="title">IT Manager</td>
                      <td>
                        <a href="#" data-id={page.manager.id} className="quick-sidebar-manager" onClick={(ev) => ev.preventDefault()}>
                          {page.manager.name}
                        </a>
                      </td>
                    </tr>
                  )}

                  {page.assigneeChanges > 0 && (
                    <tr>
                      <td className="title">Changed Technicians</td>
                      <td className="color-primary">
                        <strong>{page.assigneeChanges}</strong>
                      </td>
                    </tr>
                  )}

                  {page.technicians.length > 0 && (
                    <tr>
                      {/* Fara `class="title"`, ca in macheta veche. */}
                      <td>Assigned Technician</td>
                      <td style={{ maxHeight: 50 }}>
                        <Technicians people={page.technicians} />
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </td>

            <td className="align-right align-top">
              <LastWeek count={page.lastWeekRequests} />
            </td>
          </tr>
        </tbody>
      </table>
    </Section>
  )
}

/**
 * "Registered user" si "Subscription" - randurile omului afectat.
 *
 * Doua feluri de tichet, ca in macheta veche (`summary.php`):
 *
 *   - pe un OM: un singur rand, numele lui si abonamentul lui;
 *   - pe MASINI: cate o pereche de randuri pentru FIECARE masina, cu
 *     proprietarul ei si abonamentul proprietarului. Ramura asta lipsea din
 *     v4 - se credea ca macheta veche n-o deseneaza, dar `is_array($affected
 *     ['affected'])` e chiar cazul masinilor. Emailul proprietarului era intr-un
 *     tooltip de Bootstrap (`data-toggle="tooltip"`), legat de jQuery; aici e
 *     `title`-ul nativ al browserului, fara nicio biblioteca.
 *
 * O masina fara proprietar nu primeste rand: in portal iesea o eticheta goala
 * si "0 days remaining", adica un abonament pe care nu-l are nimeni.
 */
function Registered({ page }: { page: TicketSummaryPage }) {
  const affected = page.affected

  if (affected.type === 'Devices') {
    const owned = affected.devices.filter((device) => device.owner !== '')

    return (
      <>
        {owned.map((device) => (
          <Fragment key={device.id}>
            <tr>
              <td className="title">Registered user</td>
              <td>
                <span className="label label-danger" style={device.email !== '' ? { cursor: 'pointer' } : undefined} title={device.email !== '' ? device.email : undefined}>
                  {device.owner}
                </span>
              </td>
            </tr>
            <tr>
              <td className="title">Subscription</td>
              <td>
                <Subscription license={device.license} />
              </td>
            </tr>
          </Fragment>
        ))}
      </>
    )
  }

  if (affected.name === '' || affected.license.status === undefined) return null

  return (
    <>
      <tr>
        <td className="title">Registered user</td>
        <td>
          <span className="label label-danger">{affected.name}</span>
        </td>
      </tr>
      <tr>
        <td className="title">Subscription</td>
        <td>
          <Subscription license={affected.license} />
        </td>
      </tr>
    </>
  )
}

/** Cele patru stari ale chipului, calculate pe server (`TicketAffected`). */
function Subscription({ license }: { license: TicketLicense }) {
  if (license.status === 'expired') return <span className="label label-danger">expired</span>
  if (license.licenseType === 'unlimited') return <span className="label label-success">unlimited</span>
  if ((license.remaining_days ?? 0) > 0) return <span className="label label-success">{license.remaining_days} days remaining</span>

  return <span className="label label-danger">0 days remaining</span>
}

/**
 * Tehnicienii firmei.
 *
 * Macheta veche arata NUMAI primul, cu "Show more ..." sub el - are un `break;`
 * neconditionat in mijlocul buclei, iar restul listei il desena un script din
 * datele deja trimise in pagina. Aceeasi purtare, fara scriptul acela.
 */
function Technicians({ people }: { people: { id: number; name: string }[] }) {
  const [all, setAll] = useState(false)
  const shown = all ? people : people.slice(0, 1)

  return (
    <>
      {shown.map((person) => (
        <span key={person.id}>
          <a href="#" data-id={person.id} className="quick-sidebar-manager" onClick={(ev) => ev.preventDefault()}>
            {person.name}
          </a>
          <br />
        </span>
      ))}
      {people.length > 1 && (
        <a
          href="#"
          onClick={(ev) => {
            ev.preventDefault()
            setAll(!all)
          }}
        >
          {all ? 'Show less ...' : 'Show more ...'}
        </a>
      )}
    </>
  )
}

/**
 * Cutia din dreapta: cate cereri deschise au mai fost pe acelasi om sau pe
 * aceleasi masini in ultimele sapte zile.
 *
 * `_widgets/easeedesk/user/lastWeekHistory.php`, cu stilurile lui in linie.
 * Culoarea are DOUA trepte, nu patru: portalul scrie patru ramuri, dar `<= 4`
 * le inghite pe cele de dedesubt, deci sub cinci e totdeauna portocaliu. Se
 * pastreaza asa - altfel cifra si-ar schimba culoarea la migrare, si nimeni
 * n-ar lega una de alta.
 */
function LastWeek({ count }: { count: number }) {
  return (
    <div style={{ maxHeight: 110, textAlign: 'center', border: '2px solid #ddd', borderRadius: 5, padding: '4px 0' }}>
      <div>
        <h4 className={count >= 5 ? 'color-danger' : 'color-warning'} style={{ fontSize: 40, verticalAlign: 'top', lineHeight: '40px', marginRight: 8 }}>
          {count}
        </h4>
      </div>
      <div className="font-10" style={{ verticalAlign: 'top', lineHeight: '18px' }}>
        requests over <br /> the last 7 days
      </div>
      <div className="clearfix"></div>
    </div>
  )
}

function CurrentRequest({ id, subject, description }: { id: number; subject: string; description: string }) {
  return (
    <Section title="Current Request">
      <div style={{ marginBottom: 10 }}>
        <h4 style={{ marginBottom: 30, color: 'rgb(7, 144, 155)' }}>
          <i className="fas fa-ticket-alt" aria-hidden="true"></i> {subject}
        </h4>
        <Description id={id} html={description} />
      </div>
    </Section>
  )
}

/**
 * Descrierea, intr-un cadru al ei.
 *
 * TREBUIE sa fie un cadru, si asa era si in portal (`easeedesk/ticketDescription`
 * intr-un `<iframe>`): textul e scris intr-un editor bogat sau vine dintr-un
 * email, deci e HTML strain, cu `<style>`-urile si fonturile lui. Lipit in
 * pagina, ar rescrie stilurile portalului si invers. De-aia si scrisul lui e
 * cu serife - documentul dinauntru n-are nicio foaie de stil, exact ca acolo.
 *
 * `sandbox="allow-same-origin"` FARA `allow-scripts`: nimic dinauntru nu poate
 * rula, deci HTML-ul strain nu poate face nimic - iar pagina il poate masura, ca
 * sa creasca dupa el (`resizeIframe` + `autoResize` din portal, care faceau
 * acelasi lucru dupa `onload`). Portalul il trecea in schimb prin `xss_clean` si
 * il lasa sa ruleze; cadrul inchis e mai strans, si arata la fel.
 */
function Description({ id, html }: { id: number; html: string }) {
  const frame = useRef<HTMLIFrameElement>(null)
  const [height, setHeight] = useState(0)

  /*
   * Inaltimea se ia de la documentul dinauntru, ca in portal (`resizeIframe` +
   * `autoResize`, chemate din `onload`). Se masoara SI dupa montare, nu numai
   * pe `load`: cadrul isi ia continutul din `srcdoc`, iar `load` poate sa fi
   * trecut deja cand React leaga ascultatorul. Un cadru de inaltime zero e o
   * descriere care nu se vede deloc.
   */
  const fit = (): void => {
    const doc = frame.current?.contentDocument
    if (doc?.body) setHeight(doc.body.scrollHeight)
  }

  useEffect(fit)

  const body =
    html === ''
      ? '<div style="font-family: Calibri;background: #eee;padding:4px 10px;border-radius: 5px;font-size: 12px">No description available</div>'
      : html

  return (
    <iframe
      ref={frame}
      className="description-content"
      id={`summary-iframe-${id}`}
      title="Ticket description"
      sandbox="allow-same-origin"
      srcDoc={`<!doctype html><meta charset="utf-8"><style>body{margin:0 5px;word-wrap:normal;overflow-wrap:anywhere}img{max-width:100%}</style>${body}`}
      width="100%"
      height={height}
      style={{ border: 0, display: 'block', wordWrap: 'break-word' }}
      onLoad={fit}
    ></iframe>
  )
}
